import { isValidDateOnlyString } from '@dolfin/utils/dates'
import type { CreditSetupRuleAssignment } from '../../rpc'

export class CreditSetupRuleResolutionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CreditSetupRuleResolutionError'
  }
}

export type CreditSetupRuleAssignmentSubject = Pick<
  CreditSetupRuleAssignment,
  | 'id'
  | 'credit_setup_id'
  | 'credit_setup_rule_id'
  | 'profile_id'
  | 'team_id'
  | 'effective_from'
  | 'effective_to'
>

export type ResolveCreditSetupRuleParams = {
  assignments: CreditSetupRuleAssignmentSubject[]
  creditSetupId: number
  profileId: string | null
  effectiveTeamIds: number[]
  accrualDate: string
}

export type ResolvedCreditSetupRule = {
  assignment: CreditSetupRuleAssignmentSubject
  ruleId: number
  subject: 'profile' | 'team'
}

const isEffectiveOn = (assignment: CreditSetupRuleAssignmentSubject, date: string) =>
  assignment.effective_from <= date &&
  (assignment.effective_to == null || assignment.effective_to >= date)

export const resolveCreditSetupRule = ({
  assignments,
  creditSetupId,
  profileId,
  effectiveTeamIds,
  accrualDate
}: ResolveCreditSetupRuleParams): ResolvedCreditSetupRule | null => {
  if (!isValidDateOnlyString(accrualDate)) {
    throw new CreditSetupRuleResolutionError('accrualDate must be a valid YYYY-MM-DD value')
  }

  const scopedAssignments = assignments.filter(
    assignment => assignment.credit_setup_id === creditSetupId
  )
  const profileAssignments = scopedAssignments.filter(
    assignment =>
      profileId != null &&
      assignment.profile_id === profileId &&
      isEffectiveOn(assignment, accrualDate)
  )

  if (profileAssignments.length > 1) {
    throw new CreditSetupRuleResolutionError(
      'Multiple profile Credit Setup Rules apply to the same setup and accrual date'
    )
  }

  const profileAssignment = profileAssignments[0]
  if (profileAssignment) {
    return {
      assignment: profileAssignment,
      ruleId: profileAssignment.credit_setup_rule_id,
      subject: 'profile'
    }
  }

  const teamAssignments = scopedAssignments.filter(
    assignment =>
      assignment.team_id != null &&
      effectiveTeamIds.includes(assignment.team_id) &&
      isEffectiveOn(assignment, accrualDate)
  )
  const ruleIds = new Set(teamAssignments.map(assignment => assignment.credit_setup_rule_id))

  if (ruleIds.size === 0) return null
  if (ruleIds.size > 1) {
    throw new CreditSetupRuleResolutionError(
      'Several effective team Credit Setup Rules apply and the calculation context is ambiguous'
    )
  }

  const teamAssignment = [...teamAssignments].sort(
    (left, right) => right.effective_from.localeCompare(left.effective_from) || left.id - right.id
  )[0]
  if (!teamAssignment) return null

  return {
    assignment: teamAssignment,
    ruleId: teamAssignment.credit_setup_rule_id,
    subject: 'team'
  }
}
